import React from 'react';
import { Coins, FileText, Users, ReceiptText, FileCheck, DollarSign } from 'lucide-react';

/**
 * Компонент секции краткосрочных обязательств для формы расчета ликвидности
 */
const CurrentLiabilitiesSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  const isDetailedView = formData.settings.includeDetailedLiabilities;

  // Расчет итоговой суммы краткосрочных обязательств
  const totalCurrentLiabilities = Object.values(formData.currentLiabilities)
    .reduce((sum, value) => sum + (parseFloat(String(value).replace(/\s+/g, '').replace(',', '.')) || 0), 0);

  return (
    <div className="mb-6 bg-red-50 dark:bg-red-900/20 p-5 rounded-lg border border-red-200 dark:border-red-800">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Coins className="w-5 h-5 mr-2 text-red-500" />
        Краткосрочные обязательства
      </h3>

      {isDetailedView ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Краткосрочные заемные средства */}
          <div>
            <label htmlFor="currentLiabilities.shortTermLoans" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-1.5 text-red-500" />
              Краткосрочные заемные средства
            </label>
            <input
              type="text"
              id="currentLiabilities.shortTermLoans"
              name="currentLiabilities.shortTermLoans"
              value={formData.currentLiabilities.shortTermLoans}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentLiabilities.shortTermLoans'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentLiabilities.shortTermLoans'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentLiabilities.shortTermLoans']}
              </p>
            )}
          </div>

          {/* Кредиторская задолженность */}
          <div>
            <label htmlFor="currentLiabilities.accountsPayable" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FileText className="w-4 h-4 mr-1.5 text-red-500" />
              Кредиторская задолженность
            </label>
            <input
              type="text"
              id="currentLiabilities.accountsPayable"
              name="currentLiabilities.accountsPayable"
              value={formData.currentLiabilities.accountsPayable}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentLiabilities.accountsPayable'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentLiabilities.accountsPayable'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentLiabilities.accountsPayable']}
              </p>
            )}
          </div>

          {/* Авансы полученные */}
          <div>
            <label htmlFor="currentLiabilities.advancesReceived" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Users className="w-4 h-4 mr-1.5 text-red-500" />
              Авансы полученные
            </label>
            <input
              type="text"
              id="currentLiabilities.advancesReceived"
              name="currentLiabilities.advancesReceived"
              value={formData.currentLiabilities.advancesReceived}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentLiabilities.advancesReceived'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentLiabilities.advancesReceived'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentLiabilities.advancesReceived']}
              </p>
            )}
          </div>

          {/* Задолженность по налогам */}
          <div>
            <label htmlFor="currentLiabilities.taxesPayable" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FileCheck className="w-4 h-4 mr-1.5 text-red-500" />
              Задолженность по налогам
            </label>
            <input
              type="text"
              id="currentLiabilities.taxesPayable"
              name="currentLiabilities.taxesPayable"
              value={formData.currentLiabilities.taxesPayable}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentLiabilities.taxesPayable'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentLiabilities.taxesPayable'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentLiabilities.taxesPayable']}
              </p>
            )}
          </div>

          {/* Прочие краткосрочные обязательства */}
          <div>
            <label htmlFor="currentLiabilities.otherCurrentLiabilities" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <ReceiptText className="w-4 h-4 mr-1.5 text-red-500" />
              Прочие краткосрочные обязательства
            </label>
            <input
              type="text"
              id="currentLiabilities.otherCurrentLiabilities"
              name="currentLiabilities.otherCurrentLiabilities"
              value={formData.currentLiabilities.otherCurrentLiabilities}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['currentLiabilities.otherCurrentLiabilities'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['currentLiabilities.otherCurrentLiabilities'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['currentLiabilities.otherCurrentLiabilities']}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {/* Единственное поле при отключенной детализации */}
          <div>
            <label htmlFor="totalCurrentLiabilities" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <DollarSign className="w-4 h-4 mr-1.5 text-red-500" />
              Всего краткосрочных обязательств
            </label>
            <input
              type="text"
              id="totalCurrentLiabilities"
              value={totalCurrentLiabilities.toLocaleString('ru-RU')}
              className="form-input w-full bg-gray-100 dark:bg-gray-600"
              readOnly
            />
          </div>
        </div>
      )}

      {/* Итоговая сумма (в детализированном режиме) */}
      {isDetailedView && (
        <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-700">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Всего краткосрочных обязательств:
            </span>
            <span className="text-lg font-semibold text-red-700 dark:text-red-300">
              {totalCurrentLiabilities.toLocaleString('ru-RU')} ₽
            </span>
          </div>
        </div>
      )}

      {/* Предупреждение */}
      {totalCurrentLiabilities === 0 && (
        <div className="mt-4 bg-yellow-100 dark:bg-yellow-900/40 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <FileCheck className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Внимание: ноль обязательств
              </h4>
              <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                Сумма краткосрочных обязательств не может быть нулевой для корректного расчета коэффициентов ликвидности (деление на ноль).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Информационная подсказка */}
      <div className="mt-4 bg-red-100 dark:bg-red-900/40 p-3 rounded-lg border border-red-200 dark:border-red-800">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FileText className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
              Что включать в краткосрочные обязательства
            </h4>
            <p className="mt-1 text-xs text-red-700 dark:text-red-300">
              К краткосрочным обязательствам согласно РСБУ относятся: кредиты и займы со сроком погашения до 12 месяцев, кредиторская задолженность, авансы полученные, задолженность по налогам и сборам, а также прочие краткосрочные обязательства.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentLiabilitiesSection;